import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdProjectsSalesperson,
  GetV1CompaniesCompanyIdProjectsSalespersonData,
} from '@treadinc/horizon-api-spec';
import { noop } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';

import { FetchReason } from '~constants/enums';
import { Project } from '~hooks/useProjects/models';
import { extractPagination, Pagination } from '~services/pagination';

const derivePaginationQueryParams = (pageParam?: Pagination) => {
  return {
    'page[after]': pageParam?.after,
    'page[limit]': 20,
  };
};

type ProjectsQueryParams = {
  pagination?: Pagination;
};

type AssignedOrdersProjectsQueryContextType = {
  clearQueryData: () => void;
  data: Project[];
  fetchMore: () => void;
  fetchReason?: FetchReason;
  hasMore: boolean;
};

const AssignedOrdersProjectsQueryContext =
  createContext<AssignedOrdersProjectsQueryContextType>({
    clearQueryData: noop,
    data: [],
    fetchMore: noop,
    hasMore: false,
  });

function AssignedOrdersProjectsQueryContextProvider({
  companyId,
  children,
}: React.PropsWithChildren<{ companyId: string }>) {
  const queryKey = 'OrdersAssigned_Projects';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<ProjectsQueryParams>({});

  const query = useInfiniteQuery({
    queryKey: [queryKey],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: async ({ pageParam, signal }) => {
      const query: GetV1CompaniesCompanyIdProjectsSalespersonData['query'] = {
        ...derivePaginationQueryParams(pageParam),
      };

      const response = await getV1CompaniesCompanyIdProjectsSalesperson({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const projects = response.data.data.map((project) => Project.parse(project));
      const pagination = extractPagination(response);

      setQueryParams((state) => ({ ...state, pagination }));

      return projects;
    },
  });

  const hasMore = Boolean(queryParams.pagination?.after);

  const data = useMemo(() => (query.data?.pages ?? []).flat(), [query.data?.pages]);

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }

    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      query.fetchNextPage();
    }
  }, [hasMore]);

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return keyAsString.startsWith(queryKey);
        });
      },
    });
  }, [queryKey]);

  return (
    <AssignedOrdersProjectsQueryContext.Provider
      value={{ clearQueryData, data, fetchMore, fetchReason, hasMore }}
    >
      {children}
    </AssignedOrdersProjectsQueryContext.Provider>
  );
}

export default AssignedOrdersProjectsQueryContext;
export { AssignedOrdersProjectsQueryContextProvider };
